import { VITALL_SYSTEM } from '@constants/extension-codes';
import { BLOOD_PRESSURE } from '@constants/fhir';
import bpReferenceRange from '@constants/reference-range/blood-pressure';

import { BloodPressureRiskLevel, RiskLevel } from '@models/assessment';
import type { ObservationRecord } from '@models/observation';

import { Gender } from 'generated/types';

type AssessmentMetric = {
  risk: string;
  observation?: ObservationRecord | null;
  value?: number;
};

/**
 * Display friendly text of range and determine if metric is within range
 * @param code Metric type
 * @param metric Patient's metric risk level, observation, and value
 * @param risk Risk level (e.g. low, moderate, high) - BP, body fat, and BMI have unique risk levels
 * @param lowerLimit Lower limit of range
 * @param upperLimit Upper limit of range
 * @returns Friendly range text and if the value is within range
 */
export const displayAssessmentReferenceRange = (params: {
  code: string;
  metric: AssessmentMetric;
  risk: string;
  lowerLimit?: number;
  upperLimit?: number;
}): {
  isInRange: boolean;
  text: {
    range: string;
    id?: string;
  };
} => {
  const { lowerLimit, metric, risk, code, upperLimit } = params;
  const value =
    typeof metric.observation?.value === 'number'
      ? metric.observation.value
      : metric.value || Infinity;

  if (!upperLimit && !lowerLimit) {
    return {
      isInRange: false,
      text: {
        id: undefined,
        range: '',
      },
    };
  }

  if (code === BLOOD_PRESSURE) {
    return {
      ...displayRange(value, lowerLimit, upperLimit),
      isInRange: isWithinBpRange(metric, risk),
    };
  } else if (
    (risk === RiskLevel.Low ||
      risk === RiskLevel.Medium ||
      risk === RiskLevel.High) &&
    !metric.value
  ) {
    return {
      ...displayRange(value, lowerLimit, upperLimit),
      isInRange: risk === metric.risk,
    };
  }
  return displayRange(value, lowerLimit, upperLimit);
};

/**
 * Display friendly text of range and determine if value is within the lower and upper limits provided
 * @param value Patient's metric value
 * @param lower Lower limit of range
 * @param upper Upper limit of range
 * @returns Friendly range text and if the value is within range
 */
export const displayRange = (value: number, lower?: number, upper?: number) => {
  let response: { isInRange: boolean; text: { range: string; id?: string } } = {
    isInRange: false,
    text: {
      id: undefined,
      range: '',
    },
  };

  switch (true) {
    case !lower && !upper:
      break;
    // Upper and lower limit: Check if value is in between
    case lower && upper && lower !== upper:
      response = {
        isInRange: Boolean(value >= lower && value <= upper),
        text: {
          range: `${lower} – ${upper}`,
        },
      };
      break;
    // Lower limit only: Check is value is greater than lower limit
    case lower && !upper:
      response = {
        isInRange: Boolean(value >= lower),
        text: {
          id: 'bhaResults.above',
          range: lower.toString(),
        },
      };
      break;
    // Upper limit only: Check if value is less than upper limit
    case upper && !lower:
      response = {
        isInRange: Boolean(value <= upper),
        text: {
          id: 'bhaResults.below',
          range: upper.toString(),
        },
      };
      break;
    default:
      break;
  }

  return response;
};

/**
 * Are systolic and diastolic values within the range?
 * @param metric Patient's blood pressure assessment risk and observation (no value)
 * @param risk Provided BP risk (used to look up ref range)
 * @returns T/F: If patient's BP measurement is within range
 */
const isWithinBpRange = (metric: AssessmentMetric, risk: string): boolean => {
  const bp =
    bpReferenceRange.get(Gender.unknown)?.get(risk as BloodPressureRiskLevel) ??
    [];

  const bpMap: Record<
    string,
    { value: number; lower?: number; upper?: number }
  > = {};

  for (const comp of metric.observation?.component ?? []) {
    const vitallCode =
      comp?.code.coding?.find(({ system }) => system === VITALL_SYSTEM)?.code ??
      '';

    bpMap[vitallCode as string] = {
      value: comp?.valueQuantity?.value ?? 0,
    };
  }

  for (const ref of bp) {
    if (ref.code) {
      bpMap[ref.code] = {
        ...bpMap[ref.code],
        lower: ref.lower,
        upper: ref.upper,
      };
    }
  }

  const isWithinSystolic = displayRange(
    bpMap['systolic'].value,
    bpMap['systolic'].lower,
    bpMap['systolic'].upper
  ).isInRange;
  const isWithinDiastolic = displayRange(
    bpMap['diastolic'].value,
    bpMap['diastolic'].lower,
    bpMap['diastolic'].upper
  ).isInRange;
  switch (risk) {
    case BloodPressureRiskLevel.Hypotension:
    case BloodPressureRiskLevel.Hypertension1:
    case BloodPressureRiskLevel.Hypertension2:
    case BloodPressureRiskLevel.HypertensionCrisis:
      return isWithinSystolic || isWithinDiastolic;
    case BloodPressureRiskLevel.Normal:
    case BloodPressureRiskLevel.Elevated:
      return isWithinSystolic && isWithinDiastolic;
    default:
      return false;
  }
};
